import NavLink from 'next/link';
import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {ROLES_NAMES} from '@/src/app/constants/roles';
import useUserActions from '@/src/app/store/user/useUserActions';
import {getAuthorizationToken} from '@/src/app/utils/authorizationToken';
import useIsMobile from '@/src/hooks/useIsMobile';
import {NEW_POSITION} from '@/src/jobPortal/common/containers/SinglePositionBootstrap';
import NoAccess from '@/src/noAccess/containers/NoAccess';
import RouteContext from '@/src/routes/utils/RouteContext';
import TextLocal from '@/src/textLocal/containers/TextLocal';
import {Button, LoadingWrapper, SectionContainer} from '@/src/ui';
import GradientHeadLine from '@/src/ui/gradientHeadLine/components/GradientHeadLine';
import UnderlinedTabs from '@/src/ui/tabs/containers/UnderlinedTabs';

import css from '../styles/JobPortalActivePage.module.css';

interface JobPortalActivePageProps {
  children: JSX.Element;
  isActiveAddButton?: boolean;
}

const POSITIONS_PAGES_ID = {
  ALL_POSITIONS: 'all-positions',
  MY_POSITIONS: 'my-positions',
  ALL_CANDIDATES: 'all-candidates',
};

interface activeTabProps {
  id: string;
  isActive: boolean;
  text: string;
  description: string;
}

const JobPortalActivePage = ({
  children,
  isActiveAddButton,
}: JobPortalActivePageProps): JSX.Element => {
  const {
    // @ts-ignore
    JOB_PORTAL_MY_POSITIONS,
    // @ts-ignore
    SEARCH_ALL_CANDIDATES_ROUTE,
    // @ts-ignore
    SEARCH_ALL_POSITIONS_ROUTE,
    // @ts-ignore
    JOB_PORTAL_MY_POSITION_EDIT,
  } = useContext(RouteContext);

  const {t} = useTranslation('common');
  const [activeTab, setActiveTab] = useState<activeTabProps | null>(null);
  const {isMaxWidth1100} = useIsMobile();
  const {push, asPath} = useRouter();
  const {user} = useUserActions();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const translatedTabsList = useMemo(
    () => [
      {
        id: POSITIONS_PAGES_ID.ALL_POSITIONS,
        text: t('job-portal.all-positions'),
        description: t('job-portal.all-positions'),
        isActive: asPath.includes(SEARCH_ALL_POSITIONS_ROUTE.getLink()),
        link: SEARCH_ALL_POSITIONS_ROUTE.getLink(),
      },
      {
        id: POSITIONS_PAGES_ID.ALL_CANDIDATES,
        text: t('job-portal.all-candidates'),
        description: t('job-portal.find-the-perfect-match'),
        isActive: asPath.includes(SEARCH_ALL_CANDIDATES_ROUTE.getLink()),
        link: SEARCH_ALL_CANDIDATES_ROUTE.getLink(),
      },
      {
        id: POSITIONS_PAGES_ID.MY_POSITIONS,
        text: t('job-portal.my-positions-tab'),
        description: t('job-portal.my-positions'),
        isActive: asPath.includes(JOB_PORTAL_MY_POSITIONS.getLink()),
        link: JOB_PORTAL_MY_POSITIONS.getLink(),
      },
    ],
    [
      activeTab,
      t,
      asPath,
      SEARCH_ALL_POSITIONS_ROUTE,
      SEARCH_ALL_CANDIDATES_ROUTE,
      JOB_PORTAL_MY_POSITIONS,
    ]
  );

  useEffect(() => {
    const newActiveTab = translatedTabsList.find((tab) =>
      asPath.includes(tab.link)
    );

    // @ts-ignore
    if (activeTab?.link !== newActiveTab?.link) {
      setActiveTab(newActiveTab);
    }
  }, [activeTab, translatedTabsList, asPath]);

  const handleSelectActiveTab = useCallback((newId: number | string) => {
    const newActiveTab = translatedTabsList.find((tab) => tab.id === newId);

    push(newActiveTab.link);
  }, []);

  useEffect(() => {
    if ((getAuthorizationToken() && user.user.id) || !getAuthorizationToken()) {
      setIsLoading(false);
    }
  }, [user.user]);

  return (
    <div>
      <LoadingWrapper isLoading={isLoading} hideContentBeforeLoad>
        {user?.user?.type !== ROLES_NAMES.CLINIC ? (
          <div className={css.noAccessWrapper}>
            <NoAccess />
          </div>
        ) : (
          <>
            <div className={css.bannerWrapper}>
              <GradientHeadLine
                height={GradientHeadLine.HEIGHT.SMALL}
                isColorLight
              >
                {activeTab?.description}
              </GradientHeadLine>
            </div>
            <div>
              <SectionContainer>
                <div className={css.tabsWrapper}>
                  <UnderlinedTabs
                    tabsList={translatedTabsList}
                    onClick={handleSelectActiveTab}
                  />
                  {/* @ts-ignore */}
                  {!isMaxWidth1100 &&
                    isActiveAddButton &&
                    activeTab?.id === POSITIONS_PAGES_ID.MY_POSITIONS && (
                      <span className={css.newPositions}>
                        <NavLink
                          href={JOB_PORTAL_MY_POSITION_EDIT.getLink(
                            NEW_POSITION
                          )}
                        >
                          <a>
                            <Button
                              height={Button.HEIGHT.SMALL}
                              color={Button.COLOR.SECONDARY}
                              border={Button.BORDER.BOLD}
                              tagComponent={Button.TAG.SPAN}
                            >
                              <TextLocal id={'job-portal.add-positions'} />
                            </Button>
                          </a>
                        </NavLink>
                      </span>
                    )}
                </div>
              </SectionContainer>
            </div>
            <div>{children}</div>
          </>
        )}
      </LoadingWrapper>
    </div>
  );
};

export default JobPortalActivePage;
